import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import clsx from 'clsx';

import { ReactComponent as SelectArrow } from 'images/select-arrow.svg';
import { ReactComponent as CheckIcon } from 'images/check-icon.svg';

import Input from 'components/Input';
import InputError from 'components/InputError';

import styles from './InputSelect.module.scss';

export interface InputSelectOption {
  value: string;
  label: string;
}

interface InputSelectProps {
  placeholder?: string;
  options: InputSelectOption[];
  value: string | undefined;
  onChange: (option: InputSelectOption) => void;
  className?: string;
  name?: string;
  label?: string;
  errorMessage?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  autocomplete?: boolean;
  fallbackOption?: InputSelectOption;
}

const InputSelect: FC<InputSelectProps> = ({
  placeholder,
  options,
  value,
  onChange,
  className,
  name,
  label,
  errorMessage,
  disabled,
  onBlur,
  autocomplete = false,
  fallbackOption,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!autocomplete || !searchQuery.trim()) {
      return options;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = options.filter(
      (option) => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query),
    );

    // If no results and we have a fallback option, show only the fallback
    if (filtered.length === 0 && fallbackOption) return [fallbackOption];

    // Keep fallback option at the end of the list
    if (fallbackOption && !filtered.some((opt) => opt.value === fallbackOption.value)) {
      return [...filtered, fallbackOption];
    }

    return filtered;
  }, [options, searchQuery, autocomplete, fallbackOption]);

  // Reset search query when dropdown closes
  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  useEffect(() => {
    if (open && selectedOptionRef.current) {
      selectedOptionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [open]);

  const handleSelect = (selectedOption: InputSelectOption) => () => {
    const currentOption = options.find(({ value: selectValue }) => selectValue === selectedOption.value)!;
    onChange(currentOption);
    setSearchQuery('');
    setOpen(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (autocomplete) {
      setSearchQuery(event.currentTarget.value);
      if (!open) setOpen(true);
      return;
    }

    const currentOption = options.find(
      (selectedOption) =>
        selectedOption.value === event.currentTarget.value || selectedOption.label === event.currentTarget.value,
    )!;
    if (currentOption) {
      onChange(currentOption);
      setOpen(false);
    }
  };

  const getValue = () => {
    const currentOption = options.find((option) => option.value === value);

    return currentOption ? currentOption.label : '';
  };

  const getDisplayValue = () => {
    if (autocomplete && open) return searchQuery;

    return getValue();
  };

  const ref = useDetectClickOutside({ onTriggered: () => setOpen(false) });

  return (
    <div className={className}>
      {label && <p className={styles.label}>{label}</p>}
      <div className={clsx(styles.inputSelectContainer, autocomplete && styles.autocomplete)} ref={ref}>
        <Input
          placeholder={placeholder}
          inputClassName={styles.inputSelect}
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          onChange={(event) => handleChange(event)}
          value={getDisplayValue()}
          name={name}
          disabled={disabled}
          onBlur={onBlur}
        />
        <SelectArrow className={clsx(styles.selectArrow, open && styles.selectArrowActive)} />
        {open && (
          <ul className={styles.dropdownOptionsList}>
            {filteredOptions.map((optionItem, index) => (
              <div
                key={index}
                className={styles.dropdownOptionContainer}
                ref={optionItem.value === value ? selectedOptionRef : null}
              >
                <li className={clsx(styles.dropdownOption, styles.validOption)} onClick={handleSelect(optionItem)}>
                  {optionItem.label ? optionItem.label : optionItem.value}
                </li>
                {optionItem.value === value && (
                  <div className={styles.checkIcon}>
                    <CheckIcon />
                  </div>
                )}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className={styles.dropdownOptionContainer}>
                <li className={styles.dropdownOption}>No results found</li>
              </div>
            )}
          </ul>
        )}
        {!!errorMessage && <InputError errorMessage={errorMessage} />}
      </div>
    </div>
  );
};

export default InputSelect;
